import {
	array,
	boolean,
	file,
	isoDate,
	mimeType,
	nonEmpty,
	nullable,
	number,
	object,
	optional,
	picklist,
	pipe,
	string,
	transform,
	unknown,
	uuid,
} from "valibot";
import { manageSearchAccessAreas } from "@/lib/auth/types";
import { optionalHttpUrlSchema, requiredHttpUrlSchema } from "@/lib/helpers/url";
import { backgroundFileSchema } from "@/lib/manage/image-schemas";

export { manageSearchAccessAreas };

const manageSearchAccessFields = {
	accessArea: optional(picklist(manageSearchAccessAreas)),
	accessObjectId: optional(pipe(string(), uuid())),
};

export const eventIdSchema = object({
	eventId: pipe(string(), uuid("A valid event identifier is required.")),
});

export const addressSchema = object({
	administrativeArea: optional(string()),
	administrativeAreaCode: optional(string()),
	applePlaceId: pipe(string(), nonEmpty("A place identifier is required.")),
	coordinate: object({
		latitude: number(),
		longitude: number(),
	}),
	country: pipe(string(), nonEmpty("Country is required.")),
	formattedAddress: pipe(string(), nonEmpty("Formatted address is required.")),
	isoCountryCode: pipe(string(), nonEmpty("Country code is required.")),
	locality: pipe(string(), nonEmpty("Locality is required.")),
	postalCode: optional(string()),
	subLocality: optional(string()),
	subThoroughfare: optional(string()),
	thoroughfare: optional(string()),
});

export const referenceSchema = object({
	additionalNotes: optional(nullable(string())),
	author: optional(nullable(string())),
	bytes: optional(nullable(number())),
	url: pipe(string(), nonEmpty("A file URL is required.")),
});

export const seriesInformationSchema = object({
	eventId: pipe(string(), uuid()),
	name: pipe(string(), nonEmpty("Series name is required.")),
	shortCode: pipe(string(), nonEmpty("Series short code is required.")),
});

export const reassignSeriesSchema = object({
	eventId: pipe(string(), uuid()),
	seriesId: pipe(string(), uuid("A valid series is required.")),
});

export const generalInformationSchema = object({
	eventId: pipe(string(), uuid()),
	onlineSystemId: optional(nullable(pipe(string(), uuid()))),
	regionId: pipe(string(), uuid("Region is required.")),
	registrationSystemId: optional(nullable(pipe(string(), uuid()))),
	shortCode: pipe(string(), nonEmpty("Short code is required.")),
	tabulationSystemId: optional(nullable(pipe(string(), uuid()))),
	url: optionalHttpUrlSchema(),
});

export const socialMediaLabels = ["Facebook", "Instagram", "Threads", "YouTube", "Bluesky"] as const;

export const socialMediaLinkSchema = object({
	eventId: pipe(string(), uuid()),
	label: picklist(socialMediaLabels, "Select a platform."),
	url: requiredHttpUrlSchema(),
});

export const deleteSocialMediaSchema = object({
	eventId: pipe(string(), uuid()),
	linkId: pipe(string(), uuid()),
});

export const cloneEventSchema = eventIdSchema;

export const createEventSchema = object({
	isWorldQualifier: boolean(),
	regionId: pipe(string(), uuid("Region is required.")),
	seriesName: pipe(string(), nonEmpty("Series name is required.")),
	seriesShortCode: pipe(string(), nonEmpty("Series short code is required.")),
});

export const uploadAssetSchema = object({
	category: picklist(["logo-display", "logo-original", "file", "background"]),
	eventId: pipe(string(), uuid()),
});

export const logoFileSchema = pipe(
	file("Please select a logo file."),
	mimeType(["image/png", "image/jpeg", "image/svg+xml"], "Please select a PNG, JPEG, or SVG file.")
);

function formDataValueToOptionalString(value: FormDataEntryValue | null) {
	if (typeof value !== "string" || value.length === 0) {
		return null;
	}

	return value;
}

const addLogoFieldsSchema = object({
	additionalNotes: optional(string()),
	author: optional(string()),
	eventId: pipe(string(), uuid()),
	file: logoFileSchema,
	logoType: picklist(["display", "original"]),
	originalUrl: optional(string()),
});

export const addLogoSchema = pipe(
	unknown(),
	transform((input) => {
		if (!(input instanceof FormData)) {
			return input;
		}

		return {
			additionalNotes: formDataValueToOptionalString(input.get("additionalNotes")) ?? undefined,
			author: formDataValueToOptionalString(input.get("author")) ?? undefined,
			eventId: input.get("eventId"),
			file: input.get("file"),
			logoType: input.get("logoType"),
			originalUrl: formDataValueToOptionalString(input.get("originalUrl")) ?? undefined,
		};
	}),
	addLogoFieldsSchema
);

export const deleteLogoSchema = object({
	eventId: pipe(string(), uuid()),
	logoId: pipe(string(), uuid()),
});

export const updateBackgroundSchema = object({
	backgroundId: pipe(string(), uuid()),
	eventId: pipe(string(), uuid()),
});

export const getBackgroundSchema = object({
	backgroundId: pipe(string(), uuid()),
	...manageSearchAccessFields,
});

export const clearCustomBackgroundSchema = object({
	eventId: pipe(string(), uuid()),
});

export const setCustomBackgroundSchema = pipe(
	unknown(),
	transform((input) => {
		if (!(input instanceof FormData)) {
			return input;
		}

		return {
			eventId: input.get("eventId"),
			file: input.get("file"),
		};
	}),
	object({
		eventId: pipe(string(), uuid()),
		file: backgroundFileSchema,
	})
);

export const eventDateSchema = object({
	date: pipe(string(), isoDate("A valid date is required.")),
	dateId: pipe(string(), uuid()),
	eventId: pipe(string(), uuid()),
	label: pipe(string(), nonEmpty("Label is required.")),
	status: picklist(["scheduled", "cancelled", "rescheduled"]),
});

export const eventDateEntrySchema = object({
	date: pipe(string(), isoDate("A valid date is required.")),
	dateId: pipe(string(), uuid()),
	label: pipe(string(), nonEmpty("Label is required.")),
	status: picklist(["scheduled", "cancelled", "rescheduled"]),
});

export const updateEventDatesSchema = object({
	dates: array(eventDateEntrySchema),
	eventId: pipe(string(), uuid()),
});

export const addDateSchema = object({
	date: pipe(string(), isoDate("A valid date is required.")),
	eventId: pipe(string(), uuid()),
});

export const addDateRangeSchema = object({
	endDate: pipe(string(), isoDate("A valid end date is required.")),
	eventId: pipe(string(), uuid()),
	startDate: pipe(string(), isoDate("A valid start date is required.")),
});

export const deleteDateSchema = object({
	dateId: pipe(string(), uuid()),
	eventId: pipe(string(), uuid()),
});

export const registrationWindowSchema = object({
	acceptsDayOf: boolean(),
	capReached: boolean(),
	endDate: pipe(string(), isoDate("A valid end date is required.")),
	eventId: pipe(string(), uuid()),
	label: pipe(string(), nonEmpty("Label is required.")),
	startDate: pipe(string(), isoDate("A valid start date is required.")),
});

export const registrationWindowEntrySchema = object({
	acceptsDayOf: boolean(),
	capReached: boolean(),
	windowId: pipe(string(), uuid()),
});

export const updateRegistrationWindowsSchema = object({
	eventId: pipe(string(), uuid()),
	windows: array(registrationWindowEntrySchema),
});

export const deleteRegistrationWindowSchema = object({
	eventId: pipe(string(), uuid()),
	windowId: pipe(string(), uuid()),
});

export const addVenueSchema = object({
	address: addressSchema,
	eventId: pipe(string(), uuid()),
	label: pipe(string(), nonEmpty("Label is required.")),
});

export const deleteVenueSchema = object({
	eventId: pipe(string(), uuid()),
	venueId: pipe(string(), uuid()),
});

export const hotelBlockSchema = object({
	additionalNotes: optional(nullable(string())),
	bookingLinkUrl: optional(nullable(string())),
	bookingPhoneNumber: optional(nullable(string())),
	endDate: pipe(string(), nonEmpty("End date is required.")),
	startDate: pipe(string(), nonEmpty("Start date is required.")),
});

export const addHotelSchema = object({
	address: addressSchema,
	block: optional(nullable(hotelBlockSchema)),
	eventId: pipe(string(), uuid()),
	label: pipe(string(), nonEmpty("Label is required.")),
});

export const deleteHotelSchema = object({
	eventId: pipe(string(), uuid()),
	hotelId: pipe(string(), uuid()),
});

export const addFileSchema = object({
	additionalNotes: optional(nullable(string())),
	author: optional(nullable(string())),
	bytes: optional(nullable(number())),
	eventId: pipe(string(), uuid()),
	label: picklist(["Program", "Stage Schedule", "Days Schedule", "Syllabus"]),
	originalUrl: optional(nullable(string())),
	url: pipe(string(), nonEmpty("A file URL is required.")),
});

export const deleteFileSchema = object({
	eventId: pipe(string(), uuid()),
	fileId: pipe(string(), uuid()),
});

export const linkSiblingSchema = object({
	eventId: pipe(string(), uuid()),
	siblingEventId: pipe(string(), uuid()),
});

export const unlinkSiblingSchema = object({
	eventId: pipe(string(), uuid()),
	siblingEventId: pipe(string(), uuid()),
});

export const linkVendorSchema = object({
	eventId: pipe(string(), uuid()),
	vendorId: pipe(string(), uuid()),
});

export const unlinkVendorSchema = object({
	eventId: pipe(string(), uuid()),
	vendorId: pipe(string(), uuid()),
});

export const linkSchoolSchema = object({
	eventId: pipe(string(), uuid()),
	schoolId: pipe(string(), uuid()),
});

export const unlinkSchoolSchema = object({
	eventId: pipe(string(), uuid()),
	schoolId: pipe(string(), uuid()),
});

export const linkExamSchema = object({
	eventId: pipe(string(), uuid()),
	examId: pipe(string(), uuid()),
});

export const unlinkExamSchema = object({
	eventId: pipe(string(), uuid()),
});

export const searchQuerySchema = object({
	feisServicesOnly: optional(boolean()),
	itemType: optional(picklist(["event", "exam", "vendor", "school", "album", "artist"] as const)),
	query: string(),
	...manageSearchAccessFields,
});

export const searchEventsSchema = object({
	excludeEventId: optional(pipe(string(), uuid())),
	query: string(),
	...manageSearchAccessFields,
});

export const uploadEventFileSchema = object({
	eventId: pipe(string(), uuid()),
	file: pipe(file("Please select a PDF file."), mimeType(["application/pdf"], "Please select a PDF file.")),
});
